'use client';

import type { InteractionType } from '~/model/InteractionType';
import htmlReactParser from 'html-react-parser';
import { useLocale } from 'next-intl';
import { useState } from 'react';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { Translate } from '@/app/components/Translate/Translate';
import { getEntityTranslationVersion, getLangAttributeValue } from '@/app/utils/entities/translation';
import { Author } from '~/components/commons/Author/Author';
import { InteractionTagSticker } from '~/components/Interaction/common/InteractionTagSticker/InteractionTagSticker';
import { ComponentTranslationStatus } from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';
import { useProjectInteractionsContext } from '../provider/ProjectInteractionsProvider';
import { InteractionAside } from './InteractionAside';
import { InteractionContact } from './InteractionContact';
import { InteractionSummary } from './InteractionSummary';
import { InteractionUserStatus } from './InteractionUserStatus';

export const InteractionDetail = ({
  interaction,
}: {
  interaction: InteractionType;
}) => {
  const locale = useLocale();

  const { isAuthenticated } = useAuthenticatedContext();
  const { project } = useProjectInteractionsContext();

  const [interactionTranslated, setInteractionTranslated] = useState(false);

  const isEnTranslationValid
    = interaction?.translations?.en_GB?.status
      === ComponentTranslationStatus.valid;

  const originalLocale = interaction?.original_locale;

  const interactionData = getEntityTranslationVersion({
    entity: interaction,
    entityTranslated: interactionTranslated,
    isEnTranslationValid,
    originalLocale,
    userLanguage: AcceptLanguage[locale],
  });

  const langValue = getLangAttributeValue(
    originalLocale,
    AcceptLanguage[locale],
    interactionTranslated,
  );

  return (
    <article className="interaction-detail" data-testid="InterationDetail">
      <div className="interaction centered-content">
        <div className="interaction-header" data-testid="interaction-header">
          <InteractionTagSticker interaction={interaction} />
          <h2 lang={langValue} className="vp-title-m">
            {interactionData?.title}
          </h2>
          <Translate
            getTranslation={() => setInteractionTranslated(!interactionTranslated)}
            originalLanguage={originalLocale}
            userLanguage={AcceptLanguage[locale]}
            translated={interactionTranslated}
            entityUri={interaction?.['@id']}
            isValidTranslated={interactionData?.status === ComponentTranslationStatus.valid}
            isEnTranslationValid={isEnTranslationValid}
          />
        </div>
        <section
          className="interaction-content"
          data-testid="interaction-content"
        >
          <div className="interaction-summary">
            <InteractionSummary interaction={interaction}>
              <div className="interaction-details__statuses">
                <InteractionUserStatus interaction={interaction} />
              </div>
            </InteractionSummary>
            <div lang={langValue} className="interaction-details__description">
              {interactionData.body && htmlReactParser(interactionData.body)}
            </div>
            <div className="interaction-aside__author">
              <Author
                user={interaction?.owner}
                beginDate={interaction?.start_at}
              />
              {isAuthenticated && (
                <InteractionContact interaction={interaction} />
              )}
            </div>
          </div>
          <InteractionAside
            interaction={interaction}
            project={project}
          />
        </section>
      </div>
    </article>
  );
};
